import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bread, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [stage, setStage] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("Reset code sent if the email is registered");
      setStage(2);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { email, otp, new_password: newPassword });
      toast.success("Password reset — sign in with your new password");
      navigate("/login");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#EA580C]">
            <Bread size={20} weight="duotone" className="text-white" />
          </div>
          <span className="font-heading text-lg font-bold text-slate-900">BakeFlow</span>
        </div>
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF7ED]">
          <Key size={24} weight="duotone" className="text-[#EA580C]" />
        </div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Reset password</h2>
        <p className="mt-1 text-sm text-slate-500">
          {stage === 1 ? "Enter your email and we'll send you a reset code." : `Enter the 6-digit code sent to ${email} and choose a new password.`}
        </p>

        {stage === 1 ? (
          <form onSubmit={requestCode} className="mt-6 space-y-4" data-testid="forgot-request-form">
            {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">{error}</div>}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <Button type="submit" disabled={loading} data-testid="forgot-send-code-button" className="w-full bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
              {loading ? "Sending…" : "Send reset code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={resetPassword} className="mt-6 space-y-4" data-testid="forgot-reset-form">
            {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">{error}</div>}
            <div className="space-y-1.5">
              <Label htmlFor="otp">Reset code</Label>
              <Input id="otp" data-testid="forgot-otp-input" required maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="text-center text-lg font-semibold tracking-[0.5em]" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="newpw">New password</Label>
              <Input id="newpw" data-testid="forgot-new-password-input" type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 6 characters" />
            </div>
            <Button type="submit" disabled={loading || otp.length !== 6} data-testid="forgot-reset-button" className="w-full bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
              {loading ? "Resetting…" : "Reset password"}
            </Button>
            <button type="button" onClick={() => setStage(1)} data-testid="forgot-back-button" className="w-full text-center text-xs font-semibold text-slate-500 hover:text-slate-700">
              Use a different email
            </button>
          </form>
        )}

        <p className="mt-6 text-sm text-slate-500">
          Remembered it?{" "}
          <Link to="/login" data-testid="forgot-goto-login" className="font-semibold text-[#EA580C] hover:text-[#C2410C]">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
